import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, TrendingUp, TrendingDown, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { useRateLimit, RATE_LIMIT_CONFIGS } from "@/hooks/useRateLimit";

interface BidModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    yes_pool: number;
    no_pool: number;
    deadline: string;
  } | null;
  onBidPlaced: () => void;
}

const BidModal = ({ isOpen, onClose, event, onBidPlaced }: BidModalProps) => {
  const navigate = useNavigate();
  const { user, profile, refetchProfile } = useAuth();
  const { locale, formatCurrency } = useLocale();
  const { toast } = useToast();
  const [amount, setAmount] = useState("");
  const [prediction, setPrediction] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isLimited, checkRateLimit, recordAction } = useRateLimit(RATE_LIMIT_CONFIGS.bidPlacement);

  if (!event) return null;

  const isEventEnded = new Date(event.deadline) < new Date();
  const returnTo = `/event/${event.id}`;

  // Safety net: if a logged-out user ever gets this modal open, redirect to auth.
  useEffect(() => {
    if (!isOpen) return;
    if (user) return;

    onClose();
    navigate(`/auth?returnTo=${encodeURIComponent(returnTo)}`, { state: { from: returnTo } });
  }, [isOpen, user, navigate, onClose, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || prediction === null) return;

    // Rate limit check
    if (!checkRateLimit()) {
      toast({
        title: locale.language === "pt" ? "Muitas previsões" : "Too many predictions",
        description: locale.language === "pt" 
          ? "Aguarde um momento antes de fazer outra previsão" 
          : "Please wait a moment before placing another prediction",
        variant: "destructive",
      });
      return;
    }

    const bidAmount = parseFloat(amount);
    if (isNaN(bidAmount) || bidAmount <= 0) {
      toast({
        title: locale.language === "pt" ? "Valor inválido" : "Invalid amount",
        description: locale.language === "pt" ? "Digite um valor válido" : "Please enter a valid amount",
        variant: "destructive",
      });
      return;
    }

    if (profile && bidAmount > profile.duv_coins) {
      toast({
        title: locale.language === "pt" ? "Saldo insuficiente" : "Insufficient balance",
        description: locale.language === "pt" 
          ? "Você não tem CallyCoins suficientes" 
          : "You don't have enough CallyCoins",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Check if user already has a bid on this event with the same prediction
      const { data: existingBid, error: existingBidError } = await supabase
        .from("bids")
        .select("id, amount")
        .eq("user_id", user.id)
        .eq("event_id", event.id)
        .eq("prediction", prediction)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existingBidError) throw existingBidError;

      let isNewBid = true;

      if (existingBid) {
        // Update existing bid by adding to the amount
        const currentAmount = Number((existingBid as any).amount) || 0;
        const { error: updateError } = await supabase
          .from("bids")
          .update({ amount: currentAmount + bidAmount })
          .eq("id", existingBid.id);

        if (updateError) throw updateError;
        isNewBid = false;
      } else {
        // Insert new bid (different direction or first bid)
        const { error: bidError } = await supabase.from("bids").insert({
          user_id: user.id,
          event_id: event.id,
          amount: bidAmount,
          prediction: prediction,
        });

        if (bidError) throw bidError;
      }

      // Note: Event pools and user balance are automatically updated by database triggers
      
      // Record the action for rate limiting
      recordAction();

      // Update total_bids stat (balance is deducted atomically by trigger)
      if (profile && isNewBid) {
        await supabase
          .from("profiles")
          .update({ total_bids: profile.total_bids + 1 })
          .eq("user_id", user.id);
      }

      // Success - no toast, just close modal

      // Refetch profile to update CallyCoins across the app
      await refetchProfile();

      setAmount("");
      setPrediction(null);
      onBidPlaced();
      onClose();
    } catch (error: any) {
      toast({
        title: locale.language === "pt" ? "Erro" : "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div className="glass-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">
                  {isEventEnded 
                    ? (locale.language === "pt" ? "Ops! Tarde Demais 😅" : "Oops! Too Late 😅")
                    : (locale.language === "pt" ? "Fazer Previsão" : "Make Your Prediction")
                  }
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {isEventEnded ? (
                <div className="text-center py-6">
                  <div className="text-6xl mb-4">⏰</div>
                  <p className="text-muted-foreground mb-2">
                    {locale.language === "pt" 
                      ? "Este evento já acabou! O trem já partiu da estação... 🚂"
                      : "This event has ended! The train has left the station... 🚂"
                    }
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    {locale.language === "pt"
                      ? "Mas não se preocupe, há muitos outros eventos esperando suas previsões incríveis!"
                      : "But don't worry, there are plenty of other events waiting for your amazing predictions!"
                    }
                  </p>
                  <Button onClick={onClose} variant="hero">
                    {locale.language === "pt" ? "Explorar Outros Eventos" : "Explore Other Events"}
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-6 line-clamp-2">
                    {event.title}
                  </p>

                  {profile && (
                    <div className="flex items-center gap-2 mb-6 p-3 rounded-lg bg-muted/50">
                      <Coins size={18} className="text-primary" />
                      <span className="text-sm text-muted-foreground">
                        {locale.language === "pt" ? "Seu saldo:" : "Your balance:"}
                      </span>
                      <span className="font-bold text-foreground">
                        {profile.duv_coins.toLocaleString()} CallyCoins
                      </span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-foreground mb-2 block">
                        {locale.language === "pt" ? "Sua previsão" : "Your prediction"}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setPrediction(true)}
                          className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                            prediction === true
                              ? "border-success bg-success/20 text-success"
                              : "border-border hover:border-success/50"
                          }`}
                        >
                          <TrendingUp size={24} />
                          <span className="font-bold">{locale.language === "pt" ? "SIM" : "YES"}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setPrediction(false)}
                          className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                            prediction === false
                              ? "border-destructive bg-destructive/20 text-destructive"
                              : "border-border hover:border-destructive/50"
                          }`}
                        >
                          <TrendingDown size={24} />
                          <span className="font-bold">{locale.language === "pt" ? "NÃO" : "NO"}</span>
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-foreground mb-1 block">
                        {locale.language === "pt" ? "Quantidade (CallyCoins)" : "Amount (CallyCoins)"}
                      </label>
                      <Input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="100"
                        min="1"
                        required
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
                        {locale.language === "pt" ? "Cancelar" : "Cancel"}
                      </Button>
                      <Button 
                        type="submit" 
                        variant={prediction === true ? "success" : prediction === false ? "danger" : "hero"}
                        className="flex-1" 
                        disabled={isLoading || prediction === null}
                      >
                        {isLoading 
                          ? (locale.language === "pt" ? "Prevendo..." : "Predicting...") 
                          : (locale.language === "pt" ? "Confirmar Previsão" : "Confirm Prediction")
                        }
                      </Button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BidModal;
